import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { assessmentApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, ClipboardCheck, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Assessment, AssessmentStatus } from '@/types/learningManagement';

export const AssessmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'assessment');
  const canEdit = hasPermission('edit', 'assessment');
  const canDelete = hasPermission('delete', 'assessment');

  // Build fetch function with filters
  const fetchAssessmentsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return assessmentApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else {
        return assessmentApi.list({
          page: params.page,
          size: params.size,
          sortBy: 'createdAt',
          sortDirection: 'desc',
        });
      }
    };
  }, [searchQuery]);

  // Use paginated list hook
  const {
    data: allAssessments,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Assessment>({
    fetchFn: fetchAssessmentsFn,
    entityName: 'assessments',
    suppress404Errors: true,
  });

  // Apply client-side filters
  const assessments = useMemo(() => {
    let filtered = allAssessments;
    if (filterStatus !== 'all') {
      filtered = filtered.filter((a) => a.status === filterStatus);
    }
    return filtered;
  }, [allAssessments, filterStatus]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (assessmentId: string) => {
      await assessmentApi.delete(assessmentId);
    },
    entityName: 'assessment',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (assessmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    await deleteMutation.mutate(assessmentId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: AssessmentStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'IN_PROGRESS':
      case 'NOT_STARTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'UNDER_REVIEW':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ARCHIVED':
      case 'COMPLETED':
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const assessmentId = (assessment: Assessment) => assessment.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Assessment Management</h1>
          <p className="text-muted-foreground mt-2">Manage all assessments</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/assessments/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Assessment
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search assessments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
                <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="NOT_STARTED">Not Started</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assessments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Assessments ({assessments.length})</CardTitle>
          <CardDescription>List of all assessments</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading assessments..." />
          ) : (error || assessments.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load assessments. Please try again or create a new one." : "Get started by creating your first assessment."}
              icon={ClipboardCheck}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/assessments/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assessments.map((assessment) => (
                    <TableRow key={assessmentId(assessment)}>
                      <TableCell className="font-medium">{assessment.title}</TableCell>
                      <TableCell>{assessment.assessmentType}</TableCell>
                      <TableCell>{assessment.difficultyLevel || 'N/A'}</TableCell>
                      <TableCell>{assessment.totalQuestions || 0}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(assessment.status)}`}>
                          {assessment.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(assessment.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/assessments/${assessmentId(assessment)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/assessments/${assessmentId(assessment)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(assessmentId(assessment))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssessmentListPage;






